import {
  useMergePatients,
  usePatientMergePlan,
} from "@admin/patientMergeQueries";
import { PatientPicker } from "@admin/PatientPicker";
import { describeMutationError } from "@api/mutationErrors";
import type { Entity } from "@entities/entitiesClient";
import { faRightLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  ConfirmDialog,
  FieldError,
  IconButton,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  SettingsCard,
  SkeletonStack,
  Stamp,
  StickyNote,
} from "@scribenote/brian-design-system";
import { formatDate } from "@utils/format-date";
import { useState } from "react";

/** "visit_stage" -> "Visit stage"; derived, not looked up, so any kind the plan sends reads. */
const moveLabel = (kind: string): string =>
  kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, " ");

/** Duplicates share a name, so the created date tells the two sides apart. */
const identifyPatient = (entity: Entity): string =>
  entity.createdAt
    ? `${entity.name} (created ${formatDate(entity.createdAt)})`
    : entity.name;

export const PatientMergePanel = () => {
  const [keepPick, setKeepPick] = useState<Entity | null>(null);
  const [absorbPick, setAbsorbPick] = useState<Entity | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [merged, setMerged] = useState<{
    keepName: string;
    absorbName: string;
    visitsMoved: number;
  } | null>(null);

  const mergeMutation = useMergePatients();
  const {
    data: plan,
    isLoading: planLoading,
    isError: planIsError,
    error: planError,
  } = usePatientMergePlan(keepPick?.id ?? null, absorbPick?.id ?? null);

  const bothPicked = !!keepPick && !!absorbPick;

  const swap = () => {
    const nextKeep = absorbPick;
    setAbsorbPick(keepPick);
    setKeepPick(nextKeep);
  };

  const closeConfirm = () => {
    mergeMutation.reset();
    setConfirming(false);
  };

  const confirmMerge = () => {
    if (!keepPick || !absorbPick) return;
    mergeMutation.mutate(
      { keepUuid: keepPick.id, absorbUuid: absorbPick.id },
      {
        onSuccess: (result) => {
          setConfirming(false);
          setMerged({
            keepName: keepPick.name,
            absorbName: absorbPick.name,
            visitsMoved: result.visits,
          });
          setKeepPick(null);
          setAbsorbPick(null);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6 font-body">
      <SettingsCard
        title="Merge duplicate patients"
        subtitle="Absorb one patient record into another. Irreversible — the absorbed record is deleted."
        data-testid="patient-merge-card"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-3">
            <div className="min-w-0 flex-1">
              <PatientPicker
                label="Keep"
                value={keepPick}
                onChange={setKeepPick}
                exclude={absorbPick?.id}
                data-testid="patient-merge-keep-picker"
              />
            </div>
            <IconButton
              aria-label="Swap keep and absorb"
              variant="outline"
              onClick={swap}
              disabled={!keepPick && !absorbPick}
              data-testid="patient-merge-swap"
              className="mb-0.5"
            >
              <FontAwesomeIcon icon={faRightLeft} aria-hidden="true" />
            </IconButton>
            <div className="min-w-0 flex-1">
              <PatientPicker
                label="Absorb"
                value={absorbPick}
                onChange={setAbsorbPick}
                exclude={keepPick?.id}
                data-testid="patient-merge-absorb-picker"
              />
            </div>
          </div>

          {bothPicked && (
            <div className="mt-2 flex flex-col gap-3">
              {planLoading ? (
                <SkeletonStack count={3} itemClassName="h-8" />
              ) : planIsError ? (
                <FieldError data-testid="patient-merge-plan-error">
                  {describeMutationError(planError, "load the merge preview")}
                </FieldError>
              ) : plan ? (
                <>
                  {plan.moves.length > 0 && (
                    <LedgerTable data-testid="patient-merge-moves">
                      <LedgerHead>
                        <LedgerRow>
                          <LedgerHeadCell>Kind</LedgerHeadCell>
                          <LedgerHeadCell align="right">Repoint</LedgerHeadCell>
                        </LedgerRow>
                      </LedgerHead>
                      <LedgerBody>
                        {plan.moves.map((move) => (
                          <LedgerRow key={move.kind}>
                            <LedgerCell>{moveLabel(move.kind)}</LedgerCell>
                            <LedgerCell mono align="right">
                              {move.repoint}
                            </LedgerCell>
                          </LedgerRow>
                        ))}
                      </LedgerBody>
                    </LedgerTable>
                  )}

                  <dl
                    className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm"
                    data-testid="patient-merge-summary"
                  >
                    <dt className="text-annotation">Visits</dt>
                    <dd className="text-right font-mono text-ink-body">
                      {plan.visits}
                    </dd>
                  </dl>

                  {plan.ownerAdopted && (
                    <p className="text-sm text-ink-body">
                      Adopts the absorbed record's owner.
                    </p>
                  )}

                  {plan.filledColumns.length > 0 && (
                    <p className="text-sm text-ink-body">
                      Takes the absorbed record's{" "}
                      {plan.filledColumns
                        .map((column) => column.replace(/_/g, " "))
                        .join(", ")}
                      .
                    </p>
                  )}

                  {plan.warnings.length > 0 && (
                    <StickyNote data-testid="patient-merge-warnings">
                      <p className="text-annotation-caps text-xs font-semibold text-ink">
                        Heads up
                      </p>
                      <ul className="mt-1 list-disc pl-4 text-sm text-ink-body">
                        {plan.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </StickyNote>
                  )}

                  <div>
                    <Button
                      variant="outline"
                      className="border-ink-stopped text-ink-stopped"
                      onClick={() => setConfirming(true)}
                      data-testid="patient-merge-open-confirm"
                    >
                      Merge…
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {merged && (
            <div
              className="flex items-center gap-3 rounded-paper border border-rule bg-card px-3 py-2"
              data-testid="patient-merge-success"
            >
              <Stamp tone="working" animateIn>
                Merged
              </Stamp>
              <p className="min-w-0 flex-1 text-sm text-ink-body">
                {merged.absorbName} merged into {merged.keepName} —{" "}
                {merged.visitsMoved} visit
                {merged.visitsMoved === 1 ? "" : "s"} moved.
              </p>
              <button
                type="button"
                onClick={() => setMerged(null)}
                className="shrink-0 text-xs font-medium text-accent-deep hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </SettingsCard>

      <ConfirmDialog
        open={confirming}
        data-testid={confirming ? "patient-merge-confirm" : undefined}
        title="Merge these patient records?"
        confirmLabel="Merge"
        destructive
        busy={mergeMutation.isPending}
        onCancel={closeConfirm}
        onConfirm={confirmMerge}
        confirmTestId="patient-merge-confirm-confirm"
        cancelTestId="patient-merge-confirm-cancel"
      >
        {keepPick && absorbPick && (
          <div className="flex flex-col gap-3 text-sm text-ink-body">
            <p>
              Absorb{" "}
              <strong className="font-medium text-ink">
                {identifyPatient(absorbPick)}
              </strong>{" "}
              into{" "}
              <strong className="font-medium text-ink">
                {identifyPatient(keepPick)}
              </strong>
              .
            </p>
            <p className="font-semibold text-ink-stopped">
              This cannot be undone. {identifyPatient(absorbPick)}&apos;s record
              will be deleted; every visit, task, measurement, memo, and file it
              held moves onto {identifyPatient(keepPick)}.
            </p>
            {plan && plan.warnings.length > 0 && (
              <p className="text-annotation">
                See the warnings above before continuing.
              </p>
            )}
            {mergeMutation.isError && (
              <FieldError data-testid="patient-merge-error">
                {describeMutationError(
                  mergeMutation.error,
                  "merge these records",
                )}
              </FieldError>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};
